import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Select, Spin, Tag } from 'antd';
import http from '../utils/http';
import { getToken } from '../utils/auth';

const normalizeDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    return value.slice(0, 10);
  }
  try {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().slice(0, 10);
    }
  } catch (err) {
    // ignore parsing error
  }
  return '';
};

const buildFormState = (source) => {
  const safe = source && typeof source === 'object' ? source : {};
  const rawEmployeeId = safe.employee_id ? String(safe.employee_id) : '';
  const employeeRefId = safe.employee_ref_id !== undefined && safe.employee_ref_id !== null ? String(safe.employee_ref_id) : '';
  return {
    // employee_id 表单值仍使用员工ID（便于下拉选择）；employee_name 用于后端落库到 employee_id(存姓名)
    employee_id: employeeRefId || (/^\d+$/.test(rawEmployeeId) ? rawEmployeeId : ''),
    employee_name: safe.employee_name || (!/^\d+$/.test(rawEmployeeId) ? rawEmployeeId : ''),
    operation_type: safe.operation_type || '',
    certificate_number: safe.certificate_number || '',
    issue_date: normalizeDate(safe.issue_date),
    expiry_date: normalizeDate(safe.expiry_date),
    issuing_authority: safe.issuing_authority || '',
    certificate_url: safe.certificate_url || '',
    certificate_name: safe.certificate_name || ''
  };
};

const fieldStyle = { padding: '8px', border: '1px solid #ccc', borderRadius: 4, width: '100%' };

const SpecialOperatorForm = ({ onSubmit, onCancel, initialData = null, employees = [] }) => {
  const [form, setForm] = useState(buildFormState(initialData));
  const [certificateFile, setCertificateFile] = useState(null);
  const [removeCertificate, setRemoveCertificate] = useState(false);
  // 新增：远程搜索状态
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [deptFilter, setDeptFilter] = useState(undefined);
  const [positionFilter, setPositionFilter] = useState(undefined);
  const debounceTimer = useRef(null);
  const lastKeywordRef = useRef('');
  const [allFetchedEmployees, setAllFetchedEmployees] = useState([]); // 缓存最新查询结果用于前端再过滤

  useEffect(() => {
    setForm(buildFormState(initialData));
    setCertificateFile(null);
    setRemoveCertificate(false);
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCertificateChange = (e) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    setCertificateFile(file);
    if (file) {
      setForm(prev => ({ ...prev, certificate_name: file.name, certificate_url: '' }));
      setRemoveCertificate(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, certificateFile, removeCertificate });
  };

  // 远程搜索函数（按姓名关键字）
  const runSearch = async (kw)=>{
    if(!kw){ setEmployeeOptions([]); setAllFetchedEmployees([]); return; }
    setEmpLoading(true);
    try {
      const params = { name: kw };
      if(deptFilter) params.department = deptFilter;
      if(positionFilter) params.position = positionFilter;
      const res = await http.get('/employees', { params, headers:{ 'Authorization':'Bearer '+getToken() }});
      const list = Array.isArray(res.data)? res.data: [];
      setAllFetchedEmployees(list);
      const opts = list.slice(0,50).map(emp=>({ value: String(emp.id), label: `${emp.name}${emp.position? '（'+emp.position+'）':''}` }));
      setEmployeeOptions(opts);
    } catch(e){ /* ignore */ }
    setEmpLoading(false);
  };

  const searchEmployees = useCallback((kw)=>{
    lastKeywordRef.current = kw;
    if(debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(()=> runSearch(kw), 300);
  }, [deptFilter, positionFilter]);

  // 前端根据 dept/position 改变时如果已有 keyword 立即重新搜索
  useEffect(()=>{ if(lastKeywordRef.current){ runSearch(lastKeywordRef.current); } }, [deptFilter, positionFilter]);

  // 若初始数据提供 employee_id 且 options 为空，尝试加载该员工显示
  useEffect(()=>{
    const initialRefId = initialData?.employee_ref_id ?? initialData?.employee_id;
    if(initialRefId && employeeOptions.length===0){
      const exists = employeeOptions.some(o=> o.value===String(initialRefId));
      if(!exists){
        const label = initialData.employee_name || initialData.employee_id;
        setEmployeeOptions(prev=>[{ value:String(initialRefId), label }, ...prev]);
      }
    }
  }, [initialData, employeeOptions]);

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
      <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>选择员工 *</label>
        <Select
          showSearch
            value={form.employee_id || undefined}
            placeholder="输入姓名关键字搜索"
            filterOption={false}
            onSearch={searchEmployees}
            notFoundContent={empLoading ? <Spin size="small" /> : '无匹配'}
            options={employeeOptions}
            onChange={(val)=>{
              const emp = allFetchedEmployees.find(e=> String(e.id)===String(val));
              setForm(prev=>({
                ...prev,
                employee_id: val || '',
                employee_name: emp?.name ? String(emp.name) : prev.employee_name
              }));
            }}
            style={{ width:'100%' }}
            allowClear
        />
        {form.employee_id && (
          <div style={{ marginTop:4, fontSize:12, color:'#475569', display:'flex', gap:8, flexWrap:'wrap' }}>
            <Tag color="blue" style={{ margin:0 }}>ID: {form.employee_id}</Tag>
            {form.employee_name && <Tag color="cyan" style={{ margin:0 }}>姓名: {form.employee_name}</Tag>}
            {(()=>{ const emp = allFetchedEmployees.find(e=> String(e.id)===String(form.employee_id)); if(emp){ return <>
              {emp.department && <Tag color="geekblue" style={{ margin:0 }}>{emp.department}</Tag>}
              {emp.position && <Tag color="purple" style={{ margin:0 }}>{emp.position}</Tag>}
            </>; } return null; })()}
          </div>
        )}
      </div>

      <div style={{ display:'flex', gap:12, gridColumn:'span 2' }}>
        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
          <label style={{ fontSize:13, color:'#4b5563' }}>部门过滤</label>
          <Select allowClear placeholder="部门" value={deptFilter} onChange={v=> setDeptFilter(v)} showSearch filterOption={(i,o)=> (o?.label||'').toLowerCase().includes(i.toLowerCase())} options={[...Array.from(new Set(allFetchedEmployees.map(e=>e.department).filter(Boolean)))].map(d=>({ value:d, label:d }))} />
        </div>
        <div style={{ flex:1, display:'flex', flexDirection:'column', gap:6 }}>
          <label style={{ fontSize:13, color:'#4b5563' }}>岗位过滤</label>
          <Select allowClear placeholder="岗位" value={positionFilter} onChange={v=> setPositionFilter(v)} showSearch filterOption={(i,o)=> (o?.label||'').toLowerCase().includes(i.toLowerCase())} options={[...Array.from(new Set(allFetchedEmployees.map(e=>e.position).filter(Boolean)))].map(p=>({ value:p, label:p }))} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>作业类别 *</label>
        <input
          name="operation_type"
          value={form.operation_type}
          onChange={handleChange}
          placeholder="如：起重机司机"
          required
          style={fieldStyle}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>证书编号 *</label>
        <input
          name="certificate_number"
          value={form.certificate_number}
          onChange={handleChange}
          required
          style={fieldStyle}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>发证日期 *</label>
        <input
          name="issue_date"
          type="date"
          value={form.issue_date}
          onChange={handleChange}
          required
          style={fieldStyle}
        />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>有效期至 *</label>
        <input
          name="expiry_date"
          type="date"
          value={form.expiry_date}
          onChange={handleChange}
          required
          style={fieldStyle}
        />
      </div>

      <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>发证机关</label>
        <input
          name="issuing_authority"
          value={form.issuing_authority}
          onChange={handleChange}
          style={fieldStyle}
          placeholder="如：市市场监督管理局"
        />
      </div>

      <div style={{ gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <label style={{ fontSize: 13, color: '#4b5563' }}>证件 PDF（最大 20MB，支持 pdf）</label>
        {form.certificate_url && !certificateFile && !removeCertificate && (
          <a
            href={form.certificate_url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#2563eb', fontSize: 13 }}
          >
            {form.certificate_name || '查看已上传文件'}
          </a>
        )}
        <input type="file" accept="application/pdf" onChange={handleCertificateChange} />
        {form.certificate_url && !certificateFile && (
          <label style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="checkbox"
              checked={removeCertificate}
              onChange={(e) => setRemoveCertificate(e.target.checked)}
            />
            移除此证件 PDF
          </label>
        )}
        {certificateFile && (
          <span style={{ fontSize: 12, color: '#6b7280' }}>已选择：{certificateFile.name}</span>
        )}
      </div>

      <div style={{ gridColumn: 'span 2', display: 'flex', justifyContent: 'center', gap: 16 }}>
        <button type="submit" style={{ padding: '10px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 4 }}>
          保存
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '10px 20px', background: '#e5e7eb', color: '#374151', border: 'none', borderRadius: 4 }}>
          取消
        </button>
      </div>
    </form>
  );
};

export default SpecialOperatorForm;
